import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { BarList, TrendChart } from '@/components/ui/Charts';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/app/PlaceholderPage';
import { analyticsApi } from './api';
import { downloadCsv } from '@/lib/csv';
import { round1, shortPeriod } from './format';

/** L-15 — Perbandingan antar TBM dan tren rata-rata lintas semester. */
export function ComparisonPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['analytics', 'trend'],
    queryFn: () => analyticsApi.trend(),
  });

  // Matriks TBM × semester dirakit di klien: datanya paling banyak beberapa
  // ratus baris, dan endpoint-nya tetap sederhana untuk dipakai ulang.
  const matrix = useMemo(() => {
    if (!data) return null;
    const semesters = [...new Set(data.series.map((s) => s.semester))].sort((a, b) => a - b);
    const byTbm = new Map<string, { code: string; name: string; vals: Map<number, number | null> }>();
    for (const s of data.series) {
      const key = s.tbmId ?? s.tbmCode;
      if (!byTbm.has(key)) {
        byTbm.set(key, {
          code: s.tbmCode,
          name: s.tbmName || s.tbmCode,
          vals: new Map(),
        });
      }
      byTbm.get(key)!.vals.set(s.semester, s.index);
    }
    return { semesters, rows: [...byTbm.values()] };
  }, [data]);

  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;

  const exportCsv = () => {
    if (!data || !matrix) return;
    downloadCsv(
      'perbandingan-tbm.csv',
      [
        ['Nama TBM', 'Kode TBM', ...matrix.semesters.map((s) => `Semester ${s}`)],
        ...matrix.rows.map((row) => [
          row.name,
          row.code,
          ...matrix.semesters.map((s) => {
            const v = row.vals.get(s);
            return v === undefined || v === null ? '' : String(round1(v));
          }),
        ]),
        [],
        [
          'Rata-rata',
          '',
          ...matrix.semesters.map((s) => {
            const p = data.periods.find((x) => x.semester === s);
            return p ? String(round1(p.mean)) : '';
          }),
        ],
      ],
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Perbandingan & Tren"
        description="Pergerakan indeks seluruh TBM dampingan lintas semester."
        action={
          matrix &&
          matrix.rows.length > 0 && (
            <Button variant="secondary" onClick={exportCsv}>
              Unduh CSV
            </Button>
          )
        }
      />

      {isLoading || !data || !matrix ? (
        <Skeleton className="h-72 w-full" />
      ) : data.periods.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon="📈"
            title="Belum ada data untuk dibandingkan"
            description="Tren baru muncul setelah ada penilaian yang dikirim di minimal satu periode."
          />
        </Card>
      ) : (
        <>
          <Card>
            <h2 className="mb-3 text-[17px] font-semibold">Rata-rata indeks lintas semester</h2>
            <TrendChart
              points={data.periods.map((p) => ({ label: shortPeriod(p.name), value: p.mean }))}
            />
            <p className="mt-2 text-[13px] text-ink-muted">
              Rata-rata dihitung hanya dari TBM yang penilaiannya sudah masuk di semester
              bersangkutan, jadi jumlah penyusunnya bisa berbeda antar titik.
            </p>
          </Card>

          <Card padded={false}>
            <h2 className="px-5 pt-5 pb-3 text-[17px] font-semibold">Matriks TBM × semester</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-ink-secondary">
                    <th className="px-5 py-2.5 font-medium">Nama TBM</th>
                    {matrix.semesters.map((s) => (
                      <th key={s} className="px-4 py-2.5 text-right font-medium">
                        Sem {s}
                      </th>
                    ))}
                    <th className="px-5 py-2.5 text-right font-medium">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((row) => {
                    const seq = matrix.semesters
                      .map((s) => row.vals.get(s))
                      .filter((v): v is number => typeof v === 'number');
                    const first = seq[0];
                    const last = seq[seq.length - 1];
                    const delta =
                      first !== undefined && last !== undefined && seq.length > 1
                        ? round1(last - first)
                        : null;
                    return (
                      <tr key={row.code} className="border-b border-border last:border-0">
                        <td className="px-5 py-2.5 text-ink">
                          <div className="flex flex-col">
                            <span className="font-medium text-ink">{row.name}</span>
                            <span className="text-xs text-ink-secondary">{row.code}</span>
                          </div>
                        </td>
                        {matrix.semesters.map((s) => {
                          const v = row.vals.get(s);
                          return (
                            <td key={s} className="tabular px-4 py-2.5 text-right text-ink">
                              {v === undefined || v === null ? (
                                <span className="text-ink-muted">—</span>
                              ) : (
                                round1(v)
                              )}
                            </td>
                          );
                        })}
                        <td
                          className={
                            'tabular px-5 py-2.5 text-right font-semibold ' +
                            (delta === null
                              ? 'text-ink-muted'
                              : delta >= 0
                                ? 'text-success'
                                : 'text-danger')
                          }
                        >
                          {delta === null ? '—' : `${delta >= 0 ? '+' : ''}${delta}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="px-5 py-3 text-[13px] text-ink-muted">
              Kolom Δ membandingkan semester paling awal dan paling akhir yang tercatat untuk TBM
              itu — bukan selalu Semester 1 ke Semester terakhir, karena tidak semua TBM masuk
              program di waktu yang sama.
            </p>
          </Card>

          <Card>
            <h2 className="mb-3 text-[17px] font-semibold">Peringkat semester terakhir</h2>
            <BarList
              items={lastSemesterRanking(data.series)}
              formatValue={(v) => String(round1(v))}
            />
          </Card>
        </>
      )}
    </div>
  );
}

function lastSemesterRanking(
  series: { semester: number; index: number | null; tbmCode: string; tbmName?: string }[],
) {
  if (series.length === 0) return [];
  const last = Math.max(...series.map((s) => s.semester));
  return series
    .filter((s) => s.semester === last && s.index !== null)
    .map((s) => ({ label: s.tbmName || s.tbmCode, value: s.index as number }))
    .sort((a, b) => b.value - a.value);
}
